/**
 * Browser tracking utilities for evaluation anti-cheating detection.
 * Generates and manages persistent browser tracking IDs stored in localStorage.
 */

const TRACKING_KEY = 'examinee:id';

/**
 * Generates a unique browser tracking ID.
 * Uses crypto.randomUUID() if available, otherwise falls back to timestamp-based ID.
 */
function generateTrackingId(): string {
  // Try to use crypto.randomUUID() for a proper UUID
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for older browsers: timestamp + random number
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  return `browser-${timestamp}-${random}`;
}

/**
 * Gets the existing browser tracking ID from localStorage, or creates a new one if it doesn't exist.
 * The tracking ID persists across sessions and page refreshes to enable cross-evaluation tracking.
 * 
 * @returns The browser tracking ID (UUID or timestamp-based string)
 */
export function getOrCreateBrowserTrackingId(): string {
  try {
    // Check if tracking ID already exists
    const existingId = localStorage.getItem(TRACKING_KEY);
    if (existingId) {
      return existingId;
    }
    
    // Generate new tracking ID and store it
    const newId = generateTrackingId();
    localStorage.setItem(TRACKING_KEY, newId);
    return newId;
  } catch (error) {
    // If localStorage is not available (e.g., private browsing), generate temporary ID
    console.warn('localStorage not available, using temporary tracking ID', error);
    return generateTrackingId();
  }
}

/**
 * Gets the existing browser tracking ID without creating a new one.
 * Returns null if no tracking ID exists yet.
 * 
 * @returns The browser tracking ID or null if not found
 */
export function getBrowserTrackingId(): string | null {
  try {
    return localStorage.getItem(TRACKING_KEY);
  } catch (error) {
    console.warn('localStorage not available', error);
    return null;
  }
}

/**
 * Clears the browser tracking ID from localStorage.
 * This should only be used for testing or privacy reset purposes.
 */
export function clearBrowserTrackingId(): void {
  try {
    localStorage.removeItem(TRACKING_KEY);
  } catch (error) {
    console.warn('localStorage not available', error);
  }
}
